import { useCallback, useMemo } from "react";

// ─── Challenge rotation order ────────────────────────────────────────────────
// For each game, classic mode first, then retail.
// Games order: spells → locations → npc → lore

const CHALLENGE_ORDER = [
  { game: "spells",    mode: "classic", path: "/guess-spell",    label: "Guess the Spell",    sublabel: "Classic" },
  { game: "spells",    mode: "retail",  path: "/guess-spell",    label: "Guess the Spell",    sublabel: "Retail" },
  { game: "locations", mode: "classic", path: "/guess-location", label: "Guess the Location", sublabel: "Classic" },
  { game: "locations", mode: "retail",  path: "/guess-location", label: "Guess the Location", sublabel: "Retail" },
  { game: "npcs",      mode: "classic", path: "/guess-npc",      label: "Guess the NPC",      sublabel: "Classic" },
  { game: "npcs",      mode: "retail",  path: "/guess-npc",      label: "Guess the NPC",      sublabel: "Retail" },
  { game: "lore",      mode: "classic", path: "/guess-lore",     label: "Guess the Lore",     sublabel: "Classic" },
  { game: "lore",      mode: "retail",  path: "/guess-lore",     label: "Guess the Lore",     sublabel: "Retail" },
];

const STORAGE_KEY = "wow-daily-progress";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getTodayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function loadProgress() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { date: getTodayKey(), completed: {} };
    const parsed = JSON.parse(raw);
    // Reset if it's a new day
    if (parsed.date !== getTodayKey()) {
      return { date: getTodayKey(), completed: {} };
    }
    return parsed;
  } catch {
    return { date: getTodayKey(), completed: {} };
  }
}

function saveProgress(progress) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  } catch {
    // silently fail if localStorage is full/unavailable
  }
}

function challengeKey(game, mode) {
  return `${game}:${mode}`;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * useNextChallenge – tracks daily challenge completion and finds the next one.
 *
 * @param {string} currentGame  – e.g. "locations", "spells", "npc", "lore"
 * @param {string} currentMode  – "classic" or "retail"
 *
 * Returns:
 *   markCompleted()   – call when the current game/mode is finished
 *   nextChallenge     – { game, mode, path, label, sublabel } or null if all done
 *   allCompleted      – boolean, true when every challenge is done today
 */
export function useNextChallenge(currentGame, currentMode) {
  const markCompleted = useCallback(() => {
    const progress = loadProgress();
    progress.completed[challengeKey(currentGame, currentMode)] = true;
    saveProgress(progress);
  }, [currentGame, currentMode]);

  const { nextChallenge, allCompleted } = useMemo(() => {
    const progress = loadProgress();

    // Also mark the current game/mode as completed when computing next
    // (the component calls this after finishing, but we want the "next" to already exclude current)
    const completedSet = { ...progress.completed };
    completedSet[challengeKey(currentGame, currentMode)] = true;

    // Find the first challenge in order that is NOT completed
    // Start searching from the challenge AFTER the current one
    const currentIdx = CHALLENGE_ORDER.findIndex(
      c => c.game === currentGame && c.mode === currentMode
    );

    // Search from after current, then wrap around
    for (let offset = 1; offset < CHALLENGE_ORDER.length; offset++) {
      const idx = (currentIdx + offset) % CHALLENGE_ORDER.length;
      const c = CHALLENGE_ORDER[idx];
      if (!completedSet[challengeKey(c.game, c.mode)]) {
        return { nextChallenge: c, allCompleted: false };
      }
    }

    return { nextChallenge: null, allCompleted: true };
  }, [currentGame, currentMode]);

  return { markCompleted, nextChallenge, allCompleted };
}

export { CHALLENGE_ORDER };